import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Star, BookOpen, Users, Eye, ArrowRight, Heart, Clock, Award } from 'lucide-react';
import { chapterService } from '../services/firebase';
import Navbar from './Navbar';
import Footer from './Footer';
import './Homepage.css';

interface HomepageProps {
  className?: string;
}

const Homepage: React.FC<HomepageProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const [readingProgress, setReadingProgress] = useState({ 
    chaptersRead: 0, 
    totalChapters: 12, 
    percentage: 0,
    currentChapter: 1
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadReadingProgress();
    }
  }, [user]);

  const loadReadingProgress = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const purchasedChapters = await chapterService.getPurchasedChapters(user.uid);
      const totalChaptersRead = purchasedChapters.length + 4; // 4 free chapters
      const percentage = Math.round((totalChaptersRead / 12) * 100);
      const currentChapter = Math.min(totalChaptersRead + 1, 12);
      
      setReadingProgress({
        chaptersRead: totalChaptersRead,
        totalChapters: 12,
        percentage,
        currentChapter
      });
    } catch (error) {
      console.error('Error loading reading progress:', error);
      // Fallback to basic progress
      setReadingProgress({
        chaptersRead: 4,
        totalChapters: 12,
        percentage: 33,
        currentChapter: 5
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReadNow = (): void => {
    window.location.href = '/chapters';
  };

  const handleDonate = async (): Promise<void> => {
    // Support author functionality has been disabled
    alert('Support author functionality is currently disabled');
  };

  const StarRating: React.FC<{ rating: number; maxRating?: number }> = ({ 
    rating, 
    maxRating = 5 
  }) => {
    return (
      <div className="star-rating">
        {[...Array(maxRating)].map((_, index) => (
          <Star
            key={index}
            size={16}
            className={index < rating ? 'star filled' : 'star empty'}
            fill={index < rating ? 'currentColor' : 'none'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`homepage ${className}`}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.pexels.com/photos/3622517/pexels-photo-3622517.jpeg"
            alt="Fantasy landscape"
            className="hero-bg-image"
          />
          <div className="hero-overlay"></div>
          <div className="floating-particles">
            {[...Array(20)].map((_, i) => (
              <div key={i} className={`particle particle-${i}`}></div>
            ))}
          </div>
        </div>
        
        <div className="hero-content">
          <motion.div
            className="book-showcase"
            initial={{ opacity: 0, x: -100, rotateY: -15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <div className="book-cover-container">
              <div className="book-cover">
                <div className="cover-glow"></div>
                <img 
                  src="/images/echoesOfLunaira.png" 
                  alt="Echoes Of Lunaria Book Cover"
                  className="cover-image"
                />
                <div className="cover-overlay">
                  <div className="cover-title">Echoes Of Lunaria</div>
                  <div className="cover-author">by A.Kh</div>
                </div>
                <div className="book-spine"></div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="book-details"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
          >
            <div className="breadcrumb">
              <span>Home</span> → <span>Dark Fantasy</span> → <span>Echoes Of Lunaria</span>
            </div>

            <h1 className="book-title">Echoes Of Lunaria</h1>
            
            <div className="author-info">
              <strong>A.K. Thorne</strong> • Author
            </div>

            <div className="ranking-badge">
              <Award size={16} />
              Dark Fantasy
            </div>

            <div className="book-meta">
              <div className="meta-item">
                <Eye size={18} />
                <span>15.2K views</span>
              </div>
              <div className="meta-item">
                <BookOpen size={18} />
                <span>12 chapters</span>
              </div>
              <div className="meta-item">
                <Users size={18} />
                <span>2.8K readers</span>
              </div>
              <div className="meta-item">
                <Clock size={18} />
                <span>~4 hours</span>
              </div>
              <div className="meta-item">
                <StarRating rating={5} />
                <span className="rating-text">(4.9/5)</span>
              </div>
              <div className="status-badge">ONGOING</div>
            </div>

            <div className="book-description">
              <p>
                When the moon shattered, the world followed.
                Kingdoms burned, skies dimmed, and the cursed rose from the ashes.
                Now, the blessed walk among ruins-marked by power, hunted by fate.
                One of them, silent and of unknown origins, carries the weight of the prophecy.
              </p>
              <p>
                But something older stirs in the dark beyond the wastelands.
                An ancient force, long sealed, now claws its way back into the world.
                As three paths entwine, blood and memory begin to echo one truth-
                The fall of the moon was never an accident. It was a beginning
              </p>
            </div>

            <div className="action-buttons">
              <motion.button
                className="read-btn"
                onClick={handleReadNow}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <BookOpen size={20} />
                START READING
                <ArrowRight size={16} />
              </motion.button>
              
              <motion.button
                className="patreon-btn"
                onClick={handleDonate}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Heart size={20} />
                SUPPORT AUTHOR
              </motion.button>
            </div>

            {user && (
              <motion.div 
                className="user-progress"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <div className="progress-info">
                  <span>Welcome back, {user.displayName || user.email}!</span>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${readingProgress.percentage}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">
                    {readingProgress.chaptersRead === 12 
                      ? 'All chapters completed! 🎉' 
                      : `Chapter ${readingProgress.currentChapter} of ${readingProgress.totalChapters} • ${readingProgress.percentage}% Complete`
                    }
                  </span>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Immerse Yourself in Lunaria</h2>
            <p>Experience the story like never before with our enhanced reading platform</p>
          </motion.div>
          
          <div className="features-grid">
            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="feature-icon">
                <BookOpen size={32} />
              </div>
              <h3>Interactive Reading</h3>
              <p>Experience the story with multiple reading modes, dark themes, and customizable settings for the perfect immersive experience.</p>
            </motion.div>

            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="feature-icon">
                <Heart size={32} />
              </div>
              <h3>Support the Author</h3>
              <p>Directly support the creation of new chapters and become part of the exclusive Lunaria community of patrons and supporters.</p>
            </motion.div>

            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="feature-icon">
                <Users size={32} />
              </div>
              <h3>Join the Community</h3>
              <p>Connect with other readers, discuss theories, share insights, and get recognized as a top supporter of the series.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Homepage;